import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './AppPromoPopup.css';

const STORAGE_KEY = 'faynet_app_popup_shown';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=com.nectarlabs.mvno.faynet.app&hl=pt_BR';
const IOS_LINK = 'https://apps.apple.com/br/app/fay-net/id6759068478';

const IconGooglePlay = () => (
  <svg width="16" height="16" viewBox="0 0 512 512" aria-hidden="true">
    <path d="M99.617 8.057a24.147 24.147 0 0 0-4.145 13.679v480.528c0 5.156 1.475 9.86 4.145 13.679l2.052-1.583 288.593-281.83v-6.412L101.669 9.63z" fill="#00d4ff" />
    <path d="M390.264 233.729 289.12 279.31 99.617 8.057c2.97-3.244 6.99-5.406 11.67-5.406 3.74 0 7.86 1.19 12.14 3.66l266.837 152.46z" fill="#00f076" />
    <path d="M390.264 279.313 123.427 431.773c-4.28 2.47-8.4 3.66-12.14 3.66-4.68 0-8.7-2.16-11.67-5.4l190.503-271.253z" fill="#ffce00" />
    <path d="m479.652 233.457-89.388-46.372L289.12 279.31l101.144 91.955 89.388-46.372c14.49-7.51 14.49-29.925 0-37.435z" fill="#ff3d57" />
  </svg>
);

const IconApple = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.06 2.098-.987 3.938-.987 1.839 0 2.362.987 3.973.948 1.637-.026 2.671-1.48 3.676-2.948 1.156-1.689 1.636-3.325 1.662-3.415-.037-.014-3.182-1.226-3.213-4.85-.028-3.03 2.474-4.483 2.585-4.549-1.415-2.081-3.617-2.312-4.389-2.361-2-.16-3.675 1.088-4.622 1.088zm3.41-3.114c.845-1.023 1.417-2.445 1.26-3.86-1.214.049-2.683.809-3.55 1.832-.786.907-1.472 2.36-1.288 3.751 1.36.106 2.73-.694 3.578-1.723z" />
  </svg>
);

function AppPromoPopup() {
  const [visible, setVisible] = useState(false);

  const close = useCallback(() => {
    setVisible(false);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // sessionStorage indisponível (ex.: navegação privada) — apenas não persiste a escolha
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth > 768) {
      return undefined;
    }

    let alreadyShown = false;
    try {
      alreadyShown = window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      alreadyShown = false;
    }

    if (alreadyShown) {
      return undefined;
    }

    const timer = window.setTimeout(() => setVisible(true), 600);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [visible, close]);

  if (!visible) {
    return null;
  }

  // Renderizado via portal direto no body: a animação de transição de página usa
  // transform, o que faria um position:fixed normal se posicionar em relação a ela.
  return createPortal(
    <div className="app-promo-popup" role="dialog" aria-modal="true" aria-label="Conheça o aplicativo FayNet">
      <button
        type="button"
        className="app-promo-popup__backdrop"
        onClick={close}
        aria-label="Fechar aviso"
      />
      <div className="app-promo-popup__card">
        <button type="button" className="app-promo-popup__close" onClick={close} aria-label="Fechar">
          ×
        </button>
        <img
          src="/index/popup.png"
          alt="Baixe o aplicativo FayNet e gerencie sua linha móvel de forma simples e prática"
          className="app-promo-popup__image"
        />
        <div className="app-promo-popup__stores">
          <a
            href={ANDROID_LINK}
            target="_blank"
            rel="noreferrer"
            className="app-promo-popup__store-btn"
            aria-label="Baixar o aplicativo FayNet na Google Play"
          >
            <IconGooglePlay />
            <span className="app-promo-popup__store-label">
              <small>Disponível no</small>
              <strong>Google Play</strong>
            </span>
          </a>
          <a
            href={IOS_LINK}
            target="_blank"
            rel="noreferrer"
            className="app-promo-popup__store-btn"
            aria-label="Baixar o aplicativo FayNet na App Store"
          >
            <IconApple />
            <span className="app-promo-popup__store-label">
              <small>Disponível na</small>
              <strong>App Store</strong>
            </span>
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AppPromoPopup;
